import { type FirebaseApp, initializeApp } from "firebase/app";

// Firebase web config is PUBLIC — VITE_ prefix is fine here (never voor secrets).
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | undefined;

/**
 * Alleen de app hier — de per-product clients (Firestore/Auth/Functions/Storage) staan elk in hun
 * eigen bestand (./db, ./auth, ./functions, ./storage). Dat is bewust: importeert een pagina alleen
 * `getDb`, dan trekt 'ie zo niet ook Storage/Functions mee de bundel in. Vóór deze split zat alles
 * in één `client.ts`, en omdat de homepage (via reviews/instellingen/acties) daar al Firestore uit
 * las, kwam de HELE Firebase-SDK (incl. upload-/callable-code die de homepage nooit gebruikt)
 * alsnog in de kritieke eerste-lading-bundel terecht.
 *
 * App Check (reCAPTCHA v3) stond hier eerder ook, maar de handhaving staat sinds 01-08-2026 op
 * "Unenforced" voor zowel Firestore als Authentication (Firebase Console-toggle, bewuste keuze —
 * zie pagespeed-lighthouse-fixes.md). ~1s scripting + ~165 KiB ongebruikte JS voor iets dat niets
 * meer afdwingt; alle beveiliging loopt al via de Firestore-rules (isAdmin/hasPerm/isOwner) en
 * server-side callables, die App Check nooit nodig hadden. Weghalen dus zonder functieverlies.
 */
export function getFirebaseApp(): FirebaseApp {
  if (!app) app = initializeApp(config);
  return app;
}

// ─── Idle-gate ───────────────────────────────────────────────────────────────
// Firebase-init trekt Firestore op. Als dat tijdens de eerste render gebeurt, concurreert het met
// LCP/TBT. Passieve startup-reads (overrides, promoties, homepage, settings) en de auth-listener
// wachten daarom op deze belofte: die lost pas op zodra de browser ná de eerste paint idle is (of
// na een harde time-out). Door-de-gebruiker-getriggerde acties (inloggen, afrekenen, admin) roepen
// Firebase direct aan en gebruiken dit NIET — die initialiseren on-demand. De belofte lost precies
// één keer op en cachet.
let idlePromise: Promise<void> | undefined;
export function firebaseIdle(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!idlePromise) {
    idlePromise = new Promise<void>((resolve) => {
      const ric = (window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }).requestIdleCallback;
      if (ric) ric(() => resolve(), { timeout: 2500 });
      else setTimeout(() => resolve(), 1200);
    });
  }
  return idlePromise;
}
